import React, { useEffect, useMemo, useState } from 'react';
import { Box, Paper } from '@mui/material';
import dayjs from 'dayjs';
import isToday from 'dayjs/plugin/isToday';
import DayContentContainer from './DayContentContainer';
import DateContainer from './DateContainer';
import DayContainer from './DayContainer';
import WeekDayHeader from './WeekDayHeader';
import InvalidDay from './InvalidDay';
import MonthNavigator from './MonthNavigator';

dayjs.extend(isToday);

const DEFAULT_DATE_CONTAINER_OBJECT = {
  rightComponent: null,
  rightComponentProps: null,
};
const DEFAULT_DAY_CELL_CONTAINER_OBJECT = {
  component: null,
  componentProps: null,
};
const DEFAULT_NAVIGATION_CONTAINER_OBJECT = {
  onlyMonth: true,
  rightComponent: null,
  rightComponentProps: null,
};

function monthGrid({ monthCursor, showLapsedWeek }) {
  const start = monthCursor.startOf('month');
  const daysInMonth = start.daysInMonth();
  const leadingEmpty = start.day();
  const dates = [];
  for (let i = 0; i < leadingEmpty; i += 1) {
    dates.push(null);
  }
  for (let d = 1; d <= daysInMonth; d += 1) {
    dates.push(start.date(d));
  }
  while (dates.length % 7 !== 0) {
    dates.push(null);
  }
  if (!showLapsedWeek) {
    // Remove weeks before the current week if viewing the current month
    if (monthCursor.isSame(dayjs(), 'month')) {
      const currentWeekStart = dayjs().startOf('week');
      return dates
        .reduce((weeks, _, i) => {
          if (i % 7 === 0) weeks.push(dates.slice(i, i + 7));
          return weeks;
        }, [])
        .filter(week =>
          week.some(day => day && !day.isBefore(currentWeekStart, 'day')),
        )
        .flat();
    }
  }
  return dates;
}

function Calendar({
  value = {},
  monthInView,
  onChange,
  onDayClick = null,
  isClickable = () => false,
  DateContainerComponent = DEFAULT_DATE_CONTAINER_OBJECT,
  getDateCellValue = () => value,
  DayCellContentComponent = DEFAULT_DAY_CELL_CONTAINER_OBJECT,
  showLapsedWeek = false,
  NavigationComponent = DEFAULT_NAVIGATION_CONTAINER_OBJECT,
}) {
  const [cursor, setCursor] = useState(monthInView);

  const cellItems = useMemo(() => {
    const raw = monthGrid({ monthCursor: cursor, showLapsedWeek });
    const monthLabel = cursor.format('YYYY-MM');
    return raw.map((cell, pos) => {
      const row = Math.floor(pos / 7);
      const col = pos % 7;
      return {
        textDate: cell ? cell.format('YYYY-MM-DD') : null,
        cellDate: cell,
        gridKey: cell
          ? cell.format('YYYY-MM-DD')
          : `placeholder-${monthLabel}-r${row}-c${col}`,
      };
    });
  }, [cursor]);

  const hidePrev = !cursor.startOf('month').isAfter(dayjs(), 'month');

  const goPrev = () => {
    setCursor(c => c.subtract(1, 'month').startOf('month'));
  };

  const goNext = () => {
    setCursor(c => c.add(1, 'month').startOf('month'));
  };

  const currentDate = dayjs().startOf('day');

  useEffect(() => {
    onChange(cursor);
  }, [cursor]);

  const {
    onlyMonth,
    rightComponent: RightComponentForNavigationContainer = null,
    rightComponentProps: NavigationContainerProps = {},
  } = NavigationComponent || DEFAULT_NAVIGATION_CONTAINER_OBJECT;

  return (
    <Box>
      <Paper sx={{ p: 1, mb: 2 }} elevation={0} variant="outlined">
        {onlyMonth ? (
          <MonthNavigator
            hidePrev={hidePrev}
            goPrev={goPrev}
            cursor={cursor}
            goNext={goNext}
          />
        ) : (
          <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
            <MonthNavigator
              hidePrev={hidePrev}
              goPrev={goPrev}
              cursor={cursor}
              goNext={goNext}
            />
            {NavigationComponent?.rightComponent && (
              <RightComponentForNavigationContainer
                {...NavigationContainerProps}
              />
            )}
          </Box>
        )}
      </Paper>
      <Paper sx={{ p: 1, mb: 2 }} elevation={0} variant="outlined">
        <Box
          sx={{
            display: 'grid',
            gridTemplateColumns: 'repeat(7, 1fr)',
            gap: 0.5,
          }}>
          <WeekDayHeader />

          {cellItems.map(({ cellDate, gridKey, textDate }) => {
            if (!cellDate) {
              return <InvalidDay key={gridKey} />;
            }

            const isItToday = dayjs(cellDate).isToday();

            const {
              rightComponent: RightComponentForDateContainer = null,
              rightComponentProps = {},
            } = DateContainerComponent || DEFAULT_DATE_CONTAINER_OBJECT;

            const { component: DayCellComponent = null, componentProps = {} } =
              DayCellContentComponent || DEFAULT_DAY_CELL_CONTAINER_OBJECT;

            const cellDateValue = getDateCellValue({
              value,
              gridKey,
              cellDate,
              textDate,
              isToday: isItToday,
            });

            const isCellClickable =
              Boolean(onDayClick) &&
              isClickable({
                cellDate,
                gridKey,
                currentDate,
                isToday: isItToday,
                value,
                cellDateValue,
                textDate,
              });

            return (
              <DayContainer
                key={gridKey}
                cellDate={cellDate}
                {...(isCellClickable && {
                  onClick: () => {
                    onDayClick({
                      cellDate,
                      cellDateValue,
                      gridKey,
                      value,
                      textDate,
                      isToday: isItToday,
                      currentDate,
                    });
                  },
                })}
                isToday={isItToday}>
                <DateContainer cellDate={cellDate} isToday={isItToday}>
                  {DateContainerComponent?.rightComponent && (
                    <RightComponentForDateContainer
                      cellDate={cellDate}
                      gridKey={gridKey}
                      value={value}
                      cellDateValue={cellDateValue}
                      textDate={textDate}
                      isToday={isItToday}
                      currentDate={currentDate}
                      {...rightComponentProps}
                    />
                  )}
                </DateContainer>
                <DayContentContainer>
                  {DayCellContentComponent?.component && (
                    <DayCellComponent
                      cellDate={cellDate}
                      gridKey={gridKey}
                      value={value}
                      cellDateValue={cellDateValue}
                      textDate={textDate}
                      isToday={isItToday}
                      currentDate={currentDate}
                      {...componentProps}
                    />
                  )}
                </DayContentContainer>
              </DayContainer>
            );
          })}
        </Box>
      </Paper>
    </Box>
  );
}

export default Calendar;
